import {
  type HomeSettings,
  type LocationChoice,
  type RoutePreviewDay,
  type RoutePreviewResponse,
  departureLeadMin,
} from '@ergo/shared';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { VISIO_GLYPH } from '../appointments';
import { INLINE_DEBOUNCE_MS, useResource, useWideScreen } from '../hooks';
import { type MapPoint, useMapsKey } from '../maps';
import {
  endsAt,
  formatDuration,
  formatLongDate,
  isValidDate,
  isValidTime,
  timeOf,
} from '../time';
import { hopOf, legHome, legInto } from '../travel';
import { MapCanvas } from './MapCanvas';

export interface DayPreviewQuery {
  locationId: LocationChoice | null;
  /** The visio has no row of its own until a séance makes one: the patient says who. */
  patientId: number | null;
  /** The day being previewed — a série shows its first occurrence. */
  date: string;
  time: string;
  durationMin: number;
  /** The séance being edited: the day is previewed without it. */
  excludeId?: number;
  /** Set for a série, so the panel says which day it is showing. */
  series?: boolean;
}

/**
 * The day the séance would land in: what it costs on the bike, where it slots
 * between the others, and the two tours side by side on the map. Informative
 * like the feasibility warnings above it — every failure just hides the panel.
 */
export function DayPreview({ query }: { query: DayPreviewQuery }) {
  const [result, setResult] = useState<RoutePreviewResponse | null>(null);
  const settings = useResource(() => api.settings.get(), []);
  const maps = useMapsKey();

  const { locationId, patientId, date, time, durationMin, excludeId, series } = query;
  const ready = locationId !== null && isValidDate(date) && isValidTime(time);

  useEffect(() => {
    if (!ready || locationId === null) {
      setResult(null);
      return;
    }

    let active = true;
    const timer = setTimeout(() => {
      api.maps
        .routePreview({
          date,
          locationId,
          ...(patientId === null ? {} : { patientId }),
          startsAt: `${date}T${time}`,
          durationMin,
          ...(excludeId === undefined ? {} : { excludeId }),
        })
        .then((response) => {
          if (active) setResult(response);
        })
        .catch(() => {
          if (active) setResult(null);
        });
    }, INLINE_DEBOUNCE_MS);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [ready, locationId, patientId, date, time, durationMin, excludeId]);

  if (!result) return null;

  return (
    <section className="preview">
      <header className="preview__header">
        <h2 className="preview__title">
          {series ? `Aperçu du ${formatLongDate(result.date)}` : 'Aperçu de la journée'}
        </h2>
        {result.deltaMinutes !== null && (
          <span className="preview__chip">{deltaLabel(result.deltaMinutes)}</span>
        )}
      </header>

      {series && <p className="hint">Première séance de la série.</p>}

      <PreviewTimeline
        day={result.modified}
        located={maps.key !== null}
        home={settings.data?.home ?? null}
      />

      <PreviewMap
        result={result}
        home={settings.data?.home ?? null}
        hasKey={maps.key !== null && maps.resolved}
      />
    </section>
  );
}

/** « +15 min de vélo sur la journée », « −5 min », « +0 min ». */
function deltaLabel(deltaMinutes: number): string {
  const sign = deltaMinutes < 0 ? '−' : '+';
  return `${sign}${Math.abs(deltaMinutes)} min de vélo sur la journée`;
}

function PreviewTimeline({
  day,
  located,
  home,
}: {
  day: RoutePreviewDay;
  located: boolean;
  home: HomeSettings | null;
}) {
  const last = day.entries.at(-1);
  const back = legHome(day.legs);

  return (
    <ol className="timeline timeline--preview">
      {day.entries.map((entry) => {
        const leg = legInto(day.legs, entry.id);
        const hop = hopOf(leg, entry.startsAt);

        return (
          <Fragment key={entry.id}>
            {hop.estimate !== null && (
              <li className="timeline__entry timeline__entry--hop">
                <span className="timeline__rail">
                  <span className="timeline__when timeline__when--hop">
                    <strong>{hop.departure}</strong>
                  </span>
                  <span className="timeline__node timeline__node--hop" aria-hidden="true" />
                </span>
                <p className={`travel${hop.tight ? ' travel--tight' : ''}`}>
                  <span aria-hidden="true">🚲</span> Estimé : {hop.estimate} min à vélo
                  {leg?.fromHome && ' depuis la maison'}
                  {hop.tight && ` — l’écart n’est que de ${hop.gapMin} min`}
                </p>
              </li>
            )}

            <li
              className={`timeline__entry${entry.candidate ? ' is-candidate' : ''}`}
              aria-current={entry.candidate ? 'true' : undefined}
            >
              <span className="timeline__rail">
                <span className="timeline__when">
                  <strong>{timeOf(entry.startsAt)}</strong>
                  <small>{formatDuration(entry.durationMin)}</small>
                </span>
                <span className="timeline__node" aria-hidden="true" />
              </span>

              <div className="timeline__item">
                <span className="timeline__body">
                  <strong className="timeline__patient">
                    {entry.patientName}
                    {entry.candidate && <span className="badge">Cette séance</span>}
                  </strong>
                  <span className="muted">
                    {entry.locationKind === 'visio' && (
                      <span aria-hidden="true">{VISIO_GLYPH} </span>
                    )}
                    {entry.locationLabel}
                  </span>
                  {located && entry.locationKind !== 'visio' && entry.lat === null && (
                    <span className="muted timeline__address">Adresse non localisée</span>
                  )}
                </span>
              </div>
            </li>
          </Fragment>
        );
      })}

      {last && back && (home?.address ?? '') !== '' && (
        <li className="timeline__entry timeline__entry--hop">
          <span className="timeline__rail">
            <span className="timeline__when timeline__when--hop">
              <strong>{timeOf(endsAt(last.startsAt, last.durationMin))}</strong>
            </span>
            <span className="timeline__node timeline__node--hop" aria-hidden="true" />
          </span>
          <p className="travel">
            <span aria-hidden="true">🚲</span> Rentrer à la maison
            {back.bikeMinutes !== null && (
              <>
                {' — '}Estimé : {departureLeadMin(back.bikeMinutes)} min à vélo
              </>
            )}
          </p>
        </li>
      )}
    </ol>
  );
}

/**
 * The two tours: the day as it stands in grey underneath, the day with this
 * séance in the accent colour above it. Behind a button on a phone, where the
 * form matters more than the map.
 */
function PreviewMap({
  result,
  home,
  hasKey,
}: {
  result: RoutePreviewResponse;
  home: HomeSettings | null;
  hasKey: boolean;
}) {
  const wide = useWideScreen();
  const [open, setOpen] = useState(false);

  const points = useMemo(() => tourPoints(result.modified, home), [result.modified, home]);
  const base = useMemo(() => tourPoints(result.baseline, home), [result.baseline, home]);

  if (!hasKey || points.length === 0) return null;

  if (!wide && !open) {
    return (
      <button type="button" className="button button--small" onClick={() => setOpen(true)}>
        Voir la tournée
      </button>
    );
  }

  return <MapCanvas points={points} base={base} route title="Tournée de la journée" />;
}

/** A tour's pins, « Maison » first when it is located and the day has any stop. */
function tourPoints(day: RoutePreviewDay, home: HomeSettings | null): MapPoint[] {
  const stops = day.entries.flatMap<MapPoint>((entry) =>
    entry.lat === null || entry.lng === null
      ? []
      : [
          {
            lat: entry.lat,
            lng: entry.lng,
            title: `${timeOf(entry.startsAt)} · ${entry.patientName} — ${entry.locationLabel}`,
            encodedPolyline: legInto(day.legs, entry.id)?.encodedPolyline ?? null,
            ...(entry.candidate ? { candidate: true } : {}),
          },
        ],
  );

  if (stops.length === 0 || home?.lat == null || home.lng == null) return stops;
  const house: MapPoint = {
    lat: home.lat,
    lng: home.lng,
    home: true,
    title: `Maison — ${home.formattedAddress ?? home.address}`,
  };

  const back = legHome(day.legs);
  return [house, ...stops, ...(back ? [{ ...house, encodedPolyline: back.encodedPolyline }] : [])];
}
